/**
 * Auth-клиент kikayoo (Фаза 3, И3).
 *
 * Обёртки над fetch для `/api/auth/*` (контракт 1:1 с
 * [`auth_handlers.go`](../server/internal/api/auth_handlers.go)). Сессия хранится в
 * httpOnly cookie `kikayoo_session` — фронт её НЕ читает, поэтому каждый запрос идёт
 * с `credentials:'include'` (см. [`request`](./api.ts) в api.ts — там это выставлено
 * по умолчанию для всех вызовов). Все пути относительные (`/api/...`): в dev Vite
 * проксирует на Go-сервер, в проде — тот же origin.
 *
 * Единообразие ошибок (безопасность):
 * - `login`: неверный пароль И несуществующий email → одинаковый 401
 *   `{"error":"invalid email or password"}` (без оракула существования email).
 * - `forgot`: всегда 200 (существование email не раскрывается).
 * - `reset`: невалидный/просроченный/использованный токен → 400.
 *
 * Низкоуровневый [`request`](./api.ts) парсит JSON-тело ошибки `{ "error": string }`
 * и бросает [`ApiError`](./api.ts) с `.status` и `.message`. Экраны ловят `ApiError` и
 * по статусу решают, какое сообщение показать пользователю.
 */
import { ApiError, JSON_HEADERS, request, type CaptchaSolution } from './api';

/**
 * Системная роль пользователя (Фаза 7, И1). `'admin'` — доступ к `/admin` и
 * `/api/admin/*` (server-гейт `RequireAdmin`); `'user'` — обычный хост.
 * Приходит из `/api/auth/me`, `/login`, `/register` в поле `role` (backend
 * добавил его в `AuthUser`-DTO вместе с ролями/блокировками).
 */
export type UserRole = 'user' | 'admin';

/** Текущий аутентифицированный пользователь (ответ `/me`, `register`, `login`). */
export interface AuthUser {
  id: number;
  email: string;
  /** Публичный ник (3–32 руны; буквы/цифры/`_`/`-`). */
  username: string;
  /** ФИО — опц. поле регистрации (≤100 рун). */
  fullName?: string;
  /** `true` после подтверждения email; сразу после `register` — `false`. */
  verified: boolean;
  /** Системная роль (Фаза 7): `'admin'` видит `/admin`, `'user'` — нет. */
  role: UserRole;
}

/** Ответ `POST /api/auth/reset` (`{ "status": "ok" }`). */
export interface ResetStatus {
  status: string;
}

/**
 * Тело `POST /api/auth/register` (И3). `username` и `consent` обязательны;
 * `fullName` — опц. `website` (honeypot) и `ts` (метка открытия формы) — anti-spam:
 * скрытые служебные поля, имена которых НЕ документируются публично.
 */
export interface RegisterBody {
  email: string;
  password: string;
  username: string;
  fullName?: string;
  /** Обязательное согласие с политикой конфиденциальности. */
  consent: boolean;
  /** Honeypot: скрытое поле, всегда пустое у людей (заполняется только ботами). */
  website?: string;
  /** epoch-ms открытия формы (anti-spam timing). */
  ts?: number;
  /** Слайд-капча (И8): обязательна, когда серверная капча включена. Решение
   *  берётся из виджета Captcha. При CAPTCHA_ENABLED=0 виджет скрыт, поля нет. */
  captchaId?: string;
  captchaX?: number;
}

/**
 * `POST /api/auth/register` `{email,password,username,fullName?,consent,website?,ts?}`
 * → `201 {id,email,username,verified:false}` + cookie. В dev ответ включает
 * `devVerifyUrl` (ссылку подтверждения) — фронт это поле НЕ показывает пользователю.
 * 400 — невалидный email/пароль/username, согласие не получено, подозрение на бота;
 * 409 — email/username заняты (различимые сообщения: `email already registered` /
 * `username already taken`); 429 — rate-limit.
 */
export function register(body: RegisterBody, fetchImpl: typeof fetch = fetch): Promise<AuthUser> {
  return request<AuthUser>(
    '/api/auth/register',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    },
    fetchImpl,
  );
}

/** Ответ `POST /api/auth/verify` и `/resend-verification`: `{status:"ok"|"already verified"}`. */
export interface VerifyStatus {
  status: string;
  /** Только в dev: ссылка подтверждения (НЕ показывается пользователю). */
  devVerifyUrl?: string;
}

/**
 * `POST /api/auth/verify` `{token}` → `200 {status:"ok"}` (verified:true).
 * 400 — невалидный/просроченный/использованный токен. Публичный эндпоинт (сессия
 * не нужна: подтверждение идёт по одноразовой ссылке из письма). Фронтенд ловит
 * `/verify?token=<t>` и шлёт этот POST.
 */
export function verifyEmail(token: string, fetchImpl: typeof fetch = fetch): Promise<VerifyStatus> {
  return request<VerifyStatus>(
    '/api/auth/verify',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ token }),
    },
    fetchImpl,
  );
}

/**
 * `POST /api/auth/resend-verification` (нужна сессия, без тела) →
 * `200 {status:"ok"}` (письмо отправлено) / `{status:"already verified"}` (no-op).
 * В dev ответ содержит `devVerifyUrl`. 401 — без сессии; 429 — rate-limit.
 */
export function resendVerification(fetchImpl: typeof fetch = fetch): Promise<VerifyStatus> {
  return request<VerifyStatus>(
    '/api/auth/resend-verification',
    { method: 'POST', headers: JSON_HEADERS },
    fetchImpl,
  );
}

/**
 * `POST /api/auth/login` `{email,password,captchaId?,captchaX?}` → `200 {id,email}` + cookie.
 * 401 — неверные креды (или нет такого email — единый ответ); 429 — rate-limit.
 * 400 `captcha_required` — после K неудач сервер требует слайд-капчу (И8, адаптивно):
 * фронт показывает виджет и повторяет сабмит с captcha. 400 `captcha_failed` —
 * капча прислана, но не пройдена. captcha — опц.: решения слайд-капчи.
 */
export function login(
  email: string,
  password: string,
  captcha?: CaptchaSolution,
  fetchImpl: typeof fetch = fetch,
): Promise<AuthUser> {
  return request<AuthUser>(
    '/api/auth/login',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(
        captcha
          ? { email, password, captchaId: captcha.captchaId, captchaX: captcha.captchaX }
          : { email, password },
      ),
    },
    fetchImpl,
  );
}

/**
 * `POST /api/auth/logout` → `204`, сбрасывает cookie. Требует валидную сессию
 * (сервер защищён `RequireAuth`). Повторный logout уже сброшенной сессии → 401.
 */
export function logout(fetchImpl: typeof fetch = fetch): Promise<void> {
  return request<unknown>('/api/auth/logout', { method: 'POST' }, fetchImpl).then(() => undefined);
}

/**
 * `GET /api/auth/me` → `200 {id,email}` текущего пользователя; `401` без/с
 * просроченной сессией. Используется `AuthProvider`'ом при старте приложения для
 * bootstrap (определяет, залогинен ли текущий браузер).
 */
export function fetchMe(fetchImpl: typeof fetch = fetch): Promise<AuthUser> {
  return request<AuthUser>('/api/auth/me', { method: 'GET' }, fetchImpl);
}

/**
 * `POST /api/auth/forgot` `{email,captchaId?,captchaX?}` → всегда `200 {}`
 * (существование email не раскрывается). В dev для известного email может прийти
 * `{devResetUrl}` — фронт это поле НЕ показывает пользователю. 429 — rate-limit.
 * 400 `captcha_failed` — слайд-капча обязательна (анти email-flooding, И8) и не
 * пройдена/отсутствует; фронт обновляет капчу и повторяет. captcha — опц.
 */
export function requestPasswordReset(
  email: string,
  captcha?: CaptchaSolution,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  return request<unknown>(
    '/api/auth/forgot',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(
        captcha ? { email, captchaId: captcha.captchaId, captchaX: captcha.captchaX } : { email },
      ),
    },
    fetchImpl,
  ).then(() => undefined);
}

/**
 * `POST /api/auth/reset` `{token,newPassword}` → `200 {status:"ok"}`.
 * 400 — невалидный/просроченный/использованный токен, слабый пароль (<8).
 */
export function resetPassword(
  token: string,
  newPassword: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ResetStatus> {
  return request<ResetStatus>(
    '/api/auth/reset',
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ token, newPassword }),
    },
    fetchImpl,
  );
}

// Повторно экспортируем ApiError, чтобы экранам хватало одного импорта из './auth'.
export { ApiError };
